import { Inject, Injectable, InjectionToken } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { from, Observable } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import type { IRetrieveResourceChildrenQuery } from '@protocols/use-cases';

import { AssignedResourcesState } from '../state/assigned-resources.state';

export const LOAD_RESOURCE_CHILDREN_QUERY = new InjectionToken<IRetrieveResourceChildrenQuery>(
  'LOAD_RESOURCE_CHILDREN_QUERY'
);

/**
 * Lazy node loading for the shared `AssignedResourcesState` tree. The right list
 * is otherwise pure in-memory staged state — this effect exists only to fill in
 * a container's children the first time a manager descends into it.
 */
@Injectable()
export class AssignedResourcesEffects {
  constructor(
    private readonly state: AssignedResourcesState,
    @Inject(LOAD_RESOURCE_CHILDREN_QUERY)
    private readonly retrieveResourceChildrenQuery: IRetrieveResourceChildrenQuery
  ) {}

  /**
   * Ensures the given container's children are present.
   * State-first: skips when already loaded
   */
  public readonly ensureChildrenLoaded = this.state.effect((containerId$: Observable<string>) =>
    containerId$.pipe(
      withLatestFrom(this.state.containers$),
      filter(([id, containers]) => {
        const container = containers[id];
        return (
          !!container &&
          container.kind === 'resource' &&
          !container.childrenLoaded &&
          container.pendingPersistence !== 'save'
        );
      }),
      map(([id]) => id),
      tap((id) => {
        this.state.setChildrenLoading({ containerId: id, loading: true });
        // Clear-on-start guarantees consecutive failures stay distinct emissions
        // (select() dedups on reference equality), so each failure surfaces once.
        this.state.setChildrenLoadError(null);
      }),
      switchMap((id) =>
        from(this.retrieveResourceChildrenQuery.execute(id)).pipe(
          tapResponse(
            (children) => {
              this.state.setChildren({ containerId: id, children });
              this.state.setChildrenLoading({ containerId: id, loading: false });
            },
            (error: Error) => {
              // The raw exception lands in state; the consuming facade maps it
              // to an i18n key (components only ever see localized keys).
              this.state.setChildrenLoadError({ containerId: id, error });
              this.state.setChildrenLoading({ containerId: id, loading: false });
            }
          )
        )
      )
    )
  );
}
